import { useState, useMemo } from 'react'
import { createFileRoute, notFound, Link } from '@tanstack/react-router'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
} from 'recharts'
import { metaInstructorDescription, formatTermCode } from '@cougargrades/models'
import type { SectionPlus } from '@cougargrades/models/dto'
import { isNullish } from '@cougargrades/utils/nullish'
import {
  computeCourseAlert, computeInsightSentence, computeTrendDirection, computeProfessorVerdict,
} from '@cougargrades/utils/scoring'
import type { CourseAlert } from '@cougargrades/utils/scoring'
import { computeProfessorUDI, computeUDI, deriveUDIInputs, getUDIColor, getUDILabel, SCHOOL_BASELINES } from '@cougargrades/utils'
import type { EnrollmentInfoResult } from '@cougargrades/models/dto'
import { instructorDataQueryOptions, useInstructorData } from '../../lib/services/useInstructorData'
import { useSearchRMP } from '../../lib/services/useSearchRMP'
import { GRADE_COLORS } from '../../lib/gradeColors'

import { RMPLauncher } from '../../components/RMPLauncher'
import { GradeBar } from '../../components/ui/GradeBar'
import { UDIBadge } from '../../components/ui/UDIBadge'

// ─── Types ────────────────────────────────────────────────────────────────────

interface GradeCounts { A: number; B: number; C: number; D: number; F: number; W: number }
type CourseSortKey = 'gpa' | 'sections' | 'passRate' | 'wRate' | 'name'
type SortDir = 'asc' | 'desc'
const GRADE_KEYS: (keyof GradeCounts)[] = ['A', 'B', 'C', 'D', 'F', 'W']

// ─── Pure utility functions ────────────────────────────────────────────────

function aggregateSections(rows: SectionPlus[]): GradeCounts {
  return rows.reduce(
    (acc, row) => ({
      A: acc.A + (row.A ?? 0), B: acc.B + (row.B ?? 0), C: acc.C + (row.C ?? 0),
      D: acc.D + (row.D ?? 0), F: acc.F + (row.F ?? 0), W: acc.W + (row.W ?? 0),
    }),
    { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 },
  )
}

function enrollmentToGrades(enrollment: EnrollmentInfoResult[]): GradeCounts {
  const pick = (key: string) => enrollment.find(e => String(e.key).toUpperCase() === key || e.title.toUpperCase() === key)?.value ?? 0
  return { A: pick('A'), B: pick('B'), C: pick('C'), D: pick('D'), F: pick('F'), W: pick('W') }
}

function gradeGPA(g: GradeCounts): number | null {
  const t = g.A + g.B + g.C + g.D + g.F + g.W
  if (t === 0) return null
  return (g.A * 4 + g.B * 3 + g.C * 2 + g.D * 1) / t
}

function gpaColor(gpa: number | null): string {
  if (gpa == null) return '#94A3B8'
  if (gpa >= 3.5) return '#059669'
  if (gpa >= 2.5) return '#10B981'
  if (gpa >= 1.5) return '#D97706'
  return '#DC2626'
}

// ─── Route ─────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/i/$instructorName')({
  async loader(ctx) {
    const queryOptions = instructorDataQueryOptions(ctx.params.instructorName)
    const data = await ctx.context.queryClient.ensureQueryData(queryOptions)
    if (isNullish(data)) throw notFound()
    return data
  },
  head: (ctx) => {
    const data = ctx.loaderData
    const metaDescription = metaInstructorDescription({ ...(data ?? {}) })
    return {
      meta: [
        { title: `${data?.meta.fullName ?? ctx.params.instructorName} Grades & Difficulty | Unilytics` },
        { name: 'description', content: `${metaDescription}` },
      ],
    }
  },
  component: RouteComponent,
  notFoundComponent: NotFoundComponent,
})

// ─── Main component ────────────────────────────────────────────────────────

function RouteComponent() {
  const { instructorName } = Route.useParams()
  const [courseSortKey, setCourseSortKey] = useState<CourseSortKey>('gpa')
  const [courseSortDir, setCourseSortDir] = useState<SortDir>('desc')
  const [tableExpanded, setTableExpanded] = useState(false)

  // ── Data fetching (unchanged) ──
  const { data, status } = useInstructorData(instructorName)
  const { data: rmpData, status: rmpStatus } = useSearchRMP(
    `${data?.meta.firstName ?? ''} ${data?.meta.lastName ?? ''}`,
  )
  const DID_LOAD = status === 'success' && !isNullish(data)

  // ── Aggregated grades across all sections ──
  // Use data.enrollment (populated per-instructor) instead of sectionDataGrid rows (which have zero grade counts for instructors)
  const aggregated = useMemo(
    () => DID_LOAD ? enrollmentToGrades(data.enrollment) : { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 },
    [DID_LOAD, data],
  )
  const total = aggregated.A + aggregated.B + aggregated.C + aggregated.D + aggregated.F + aggregated.W
  // Use the authoritative GPA badge value rather than computing from enrollment counts (which may differ due to rounding)
  const avgGPA = DID_LOAD ? (parseFloat(data.badges.find(b => b.key === 'gpa')?.text ?? '0') || 0) : 0
  const wRatePct = total > 0 ? (aggregated.W / total) * 100 : 0
  const fRatePct = total > 0 ? (aggregated.F / total) * 100 : 0
  const uhBaseline = SCHOOL_BASELINES['uh']
  const profUDIResult = DID_LOAD ? computeProfessorUDI({
    professorAvgGPA: avgGPA,
    courseAvgGPA: uhBaseline.avgGPA,
    professorWRate: wRatePct,
    courseWRate: uhBaseline.avgWRate,
    professorSections: data.sectionCount,
    professorGrades: aggregated,
    schoolId: 'uh',
  }) : null
  const diffScore = profUDIResult?.udi ?? 5
  const diffLbl = profUDIResult?.label ?? 'Moderate'
  const diffClr = getUDIColor(diffScore)
  const abRatePct = total > 0 ? ((aggregated.A + aggregated.B) / total) * 100 : 0
  const passRatePct = total > 0 ? ((aggregated.A + aggregated.B + aggregated.C) / total) * 100 : 0
  const insightSentence = DID_LOAD ? computeInsightSentence(aggregated) : ''
  const verdict = DID_LOAD && avgGPA > 0
    ? computeProfessorVerdict({ avgGPA, wRate: wRatePct, totalSections: data.sectionCount })
    : null

  // ── Per-term trend (GPA + section count) ──
  const trendData = useMemo(() => {
    if (!DID_LOAD) return []
    const byTerm = new Map<number, { pts: number; grTotal: number; sections: number }>()
    for (const row of data.sectionDataGrid.rows) {
      const e = byTerm.get(row.term) ?? { pts: 0, grTotal: 0, sections: 0 }
      e.pts += row.A * 4 + row.B * 3 + row.C * 2 + row.D * 1
      e.grTotal += row.A + row.B + row.C + row.D + row.F + row.W
      e.sections += 1
      byTerm.set(row.term, e)
    }
    return [...byTerm.entries()]
      .sort(([a], [b]) => a - b)
      .map(([term, e]) => ({
        term: formatTermCode(term),
        gpa: e.grTotal > 0 ? Math.round((e.pts / e.grTotal) * 100) / 100 : null,
        sections: e.sections,
      }))
  }, [DID_LOAD, data])

  const trendDir = computeTrendDirection(trendData)

  // ── Per-course data from sections ──
  const courseDescMap = useMemo(() => {
    const m = new Map<string, string>()
    if (!DID_LOAD) return m
    for (const row of data.courseDataGrid.rows) {
      m.set(row.id, row.description ?? '')
    }
    return m
  }, [DID_LOAD, data])

  const courseData = useMemo(() => {
    if (!DID_LOAD) return []
    type Entry = { grades: GradeCounts; sectionCount: number; lastTerm: number }
    const byCourse = new Map<string, Entry>()
    for (const row of data.sectionDataGrid.rows) {
      const e = byCourse.get(row.courseName) ?? { grades: { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }, sectionCount: 0, lastTerm: 0 }
      e.grades.A += row.A; e.grades.B += row.B; e.grades.C += row.C
      e.grades.D += row.D; e.grades.F += row.F; e.grades.W += row.W
      e.sectionCount += 1
      e.lastTerm = Math.max(e.lastTerm, row.term)
      byCourse.set(row.courseName, e)
    }
    return [...byCourse.entries()].map(([code, e]) => {
      const gpa = gradeGPA(e.grades)
      const t = e.grades.A + e.grades.B + e.grades.C + e.grades.D + e.grades.F + e.grades.W
      return {
        code,
        description: courseDescMap.get(code) ?? '',
        grades: e.grades,
        gpa,
        diffScore: computeUDI(deriveUDIInputs({ grades: e.grades, sections: e.sectionCount, schoolId: 'uh' })).udi,
        sectionCount: e.sectionCount,
        passRate: t > 0 ? Math.round(((e.grades.A + e.grades.B + e.grades.C) / t) * 100) : 0,
        wRate: t > 0 ? Math.round((e.grades.W / t) * 100) : 0,
        total: t,
        lastTerm: e.lastTerm,
      }
    })
  }, [DID_LOAD, data, courseDescMap])

  // ── Active window for "Courses They Teach" card (last 8 unique terms, min 3 courses) ──
  const activeCourses = useMemo(() => {
    if (!DID_LOAD || courseData.length === 0) return courseData
    const sortedTerms = [...new Set(data.sectionDataGrid.rows.map(r => r.term))].sort((a, b) => b - a)
    for (const windowSize of [8, sortedTerms.length]) {
      const windowTerms = new Set(sortedTerms.slice(0, windowSize))
      const active = courseData.filter(c => windowTerms.has(c.lastTerm))
      if (active.length >= 3 || windowSize === sortedTerms.length) return active
    }
    return courseData
  }, [DID_LOAD, data, courseData])

  // Sorted for the "Courses They Teach" hero card
  const sortedActiveCourses = useMemo(
    () => [...activeCourses].sort((a, b) => b.sectionCount - a.sectionCount),
    [activeCourses],
  )

  // ── Professor alert ──
  const profAlert = DID_LOAD && total > 0 ? computeCourseAlert({
    wRate: wRatePct,
    fRate: fRatePct,
    abRate: abRatePct,
    avgGPA,
    trend: trendDir,
    sectionCount: data.sectionCount,
    isProfessorPage: true,
  }) : null

  // ── Course breakdown table sort ──
  const handleCourseSort = (key: CourseSortKey) => {
    if (key === courseSortKey) setCourseSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setCourseSortKey(key); setCourseSortDir('desc') }
  }

  const sortedCourseTable = useMemo(() => {
    return [...courseData].sort((a, b) => {
      if (courseSortKey === 'name') return courseSortDir === 'asc' ? a.code.localeCompare(b.code) : b.code.localeCompare(a.code)
      const vals: Record<CourseSortKey, [number, number]> = {
        gpa: [a.gpa ?? -1, b.gpa ?? -1],
        sections: [a.sectionCount, b.sectionCount],
        passRate: [a.passRate, b.passRate],
        wRate: [a.wRate, b.wRate],
        name: [0, 0],
      }
      const [av, bv] = vals[courseSortKey]
      return courseSortDir === 'desc' ? bv - av : av - bv
    })
  }, [courseData, courseSortKey, courseSortDir])

  const TABLE_PREVIEW = 8
  const topGPA = Math.max(...courseData.map(c => c.gpa ?? -1), 0)
  const bottomGPA = courseData.length > 1 ? Math.min(...courseData.filter(c => c.gpa != null).map(c => c.gpa!), 99) : -1
  const visibleCourseRows = tableExpanded ? sortedCourseTable : sortedCourseTable.slice(0, TABLE_PREVIEW)

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>
      <style>{`
        @media (max-width: 640px) {
          .instr-hero-name { font-size: 26px !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ background: 'var(--school-primary)', backgroundImage: 'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Left: name + dept + chips */}
            <div className="flex-1 min-w-0">
              <div className="instr-hero-name" style={{ fontSize: 38, fontWeight: 800, color: '#fff', lineHeight: 1.1, marginBottom: 8 }}>
                {DID_LOAD ? data.meta.fullNameLastNameFirst : '…'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--school-secondary)', marginBottom: 16, lineHeight: 1.3 }}>
                {DID_LOAD ? data.meta.descriptionDepartmentsInvolved : '…'}
              </div>

              {/* Stat chips row */}
              {DID_LOAD && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <HeroChip color={gpaColor(avgGPA > 0 ? avgGPA : null)}>
                    {avgGPA > 0 ? `${avgGPA.toFixed(2)} Avg GPA` : 'No GPA data'}
                  </HeroChip>
                  <HeroChip color={diffClr}>
                    {diffScore.toFixed(1)} {diffLbl} UDI
                  </HeroChip>
                  <HeroChip>{data.sectionCount} Sections</HeroChip>
                  <HeroChip>{data.courseCount} Courses</HeroChip>
                  <HeroChip>{data.firstTaught} – {data.lastTaught}</HeroChip>
                </div>
              )}

              {/* Verdict */}
              {verdict && (
                <p style={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic', margin: '0 0 12px' }}>
                  "{verdict}"
                </p>
              )}

              {/* GPA delta stat */}
              {DID_LOAD && (() => {
                const topCourse = sortedActiveCourses[0]
                if (!topCourse || topCourse.gpa == null || topCourse.gpa <= 0) return null
                const courseRow = data.courseDataGrid.rows.find(r => r.id === topCourse.code)
                const courseAvg = courseRow?.GPA?.average
                if (courseAvg == null || courseAvg <= 0) return null
                const delta = topCourse.gpa - courseAvg
                if (Math.abs(delta) < 0.5) return null
                const sign = delta > 0 ? '+' : ''
                const text = delta > 0
                  ? `Students typically earn ${sign}${delta.toFixed(1)} pts above the course average in this professor's sections.`
                  : `Students typically earn ${Math.abs(delta).toFixed(1)} pts below the course average in this professor's sections.`
                return (
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', margin: '0 0 12px' }}>
                    {text}
                  </p>
                )
              })()}

              {/* RMP link */}
              {DID_LOAD && (
                rmpStatus === 'success' && Array.isArray(rmpData) && rmpData.length > 0
                  ? <RMPLauncher instructorFirstName={data.meta.firstName} instructorLastName={data.meta.lastName} data={rmpData} />
                  : data.rmpHref
                    ? (
                      <a
                        href={data.rmpHref}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12, color: '#94A3B8', textDecoration: 'none', borderBottom: '1px solid rgba(148,163,184,0.3)' }}
                      >
                        ⭐ View on RateMyProfessors →
                      </a>
                    )
                    : null
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-6xl mx-auto px-4">

        {/* ── PROFESSOR ALERT ── */}
        {profAlert && <ProfAlertBanner alert={profAlert} />}

        {/* ── TWO HERO CARDS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">

          {/* LEFT — Grading Profile */}
          <Card>
            <SectionTitle icon="📊">Grading Profile</SectionTitle>
            <div className="mt-5" />

            {/* Hero: UDI */}
            {DID_LOAD && profUDIResult
              ? <UDIBadge size="lg" udi={profUDIResult.udi} className="mb-5" />
              : <div style={{ height: 120, background: '#F1F5F9', borderRadius: 10, marginBottom: 20 }} />
            }

            {/* Secondary stats row */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <QuickStat value={avgGPA > 0 ? avgGPA.toFixed(2) : '—'} label="Avg GPA" color={gpaColor(avgGPA > 0 ? avgGPA : null)} />
              <QuickStat value={total > 0 ? `${wRatePct.toFixed(1)}%` : '—'} label="W Rate" color={wRatePct > 15 ? '#DC2626' : wRatePct > 8 ? '#D97706' : '#059669'} />
              <QuickStat value={total > 0 ? `${Math.round(passRatePct)}%` : '—'} label="Pass Rate" color={passRatePct > 70 ? '#059669' : passRatePct > 50 ? '#D97706' : '#DC2626'} />
            </div>

            {/* Grade distribution bar */}
            {DID_LOAD && total > 0 ? (
              <>
                <GradeBar grades={aggregated} height={14} showLabels />
                <div className="mt-4" />
              </>
            ) : (
              <div style={{ height: 14, background: '#F1F5F9', borderRadius: 10, marginBottom: 16 }} />
            )}

            {/* Insight */}
            {DID_LOAD && insightSentence && (
              <p style={{ fontSize: 13, color: '#475569', fontStyle: 'italic', margin: '0 0 16px', padding: '10px 14px', background: '#F8FAFC', borderRadius: 10, borderLeft: '3px solid var(--school-primary)' }}>
                {insightSentence}
              </p>
            )}
          </Card>

          {/* RIGHT — Courses They Teach */}
          <Card style={{ display: 'flex', flexDirection: 'column' }}>
            <SectionTitle icon="📚">Courses They Teach</SectionTitle>
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: 420, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {!DID_LOAD
                ? [1, 2, 3].map(i => <CourseRowSkeleton key={i} />)
                : sortedActiveCourses.map(course => (
                    <CourseRow key={course.code} course={course} />
                  ))
              }
            </div>
            <p style={{ fontSize: 11, color: '#CBD5E1', marginTop: 12, borderTop: '1px solid #F1F5F9', paddingTop: 10 }}>
              Showing courses taught in the last 4 years
            </p>
          </Card>
        </div>

        {/* ── TREND CHARTS ── */}
        <div className="mt-8">
          <Card>
            <div className="flex items-start justify-between mb-1">
              <SectionTitle icon="📈">How Has This Professor Graded Over Time?</SectionTitle>
              {trendDir !== 'stable' && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, color: trendDir === 'easier' ? '#059669' : '#DC2626', background: trendDir === 'easier' ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)', whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {trendDir === 'easier' ? '↑ Trending easier' : '↓ Trending harder'}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: '#94A3B8', margin: '6px 0 20px' }}>Average GPA and section count per semester</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Average GPA per Semester</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="term" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis domain={[0, 4]} tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(v: number) => v.toFixed(1)} width={32} />
                    <ReTooltip formatter={(v) => [typeof v === 'number' ? v.toFixed(2) : '—', 'Avg GPA']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                    <Line type="monotone" dataKey="gpa" stroke="var(--school-primary)" strokeWidth={2.5} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Sections per Semester</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="term" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} width={32} allowDecimals={false} />
                    <ReTooltip formatter={(v) => [v, 'Sections']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                    <Bar dataKey="sections" fill="#0D1B2A" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </div>

        {/* ── COURSE BREAKDOWN TABLE ── */}
        {DID_LOAD && courseData.length > 0 && (
          <div className="mt-8">
            <Card>
              <SectionTitle icon="🎓">Performance by Course</SectionTitle>
              <p style={{ fontSize: 13, color: '#94A3B8', margin: '6px 0 20px' }}>How this professor grades in each course they teach</p>
              <div className="overflow-x-auto">
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #F1F5F9' }}>
                      <CourseSortTh label="Course" sortKey="name" current={courseSortKey} dir={courseSortDir} onSort={handleCourseSort} align="left" />
                      <CourseSortTh label="Avg GPA" sortKey="gpa" current={courseSortKey} dir={courseSortDir} onSort={handleCourseSort} />
                      <th style={thStyle}>Difficulty</th>
                      <CourseSortTh label="Pass Rate" sortKey="passRate" current={courseSortKey} dir={courseSortDir} onSort={handleCourseSort} className="hidden sm:table-cell" />
                      <CourseSortTh label="W Rate" sortKey="wRate" current={courseSortKey} dir={courseSortDir} onSort={handleCourseSort} className="hidden sm:table-cell" />
                      <CourseSortTh label="Sections" sortKey="sections" current={courseSortKey} dir={courseSortDir} onSort={handleCourseSort} className="hidden sm:table-cell" />
                      <th style={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCourseRows.map(course => {
                      const isBest = course.gpa != null && course.gpa === topGPA && topGPA > 0
                      const isWorst = !isBest && course.gpa != null && course.gpa === bottomGPA && courseData.filter(c => c.gpa != null).length > 1
                      return (
                        <tr
                          key={course.code}
                          style={{
                            borderBottom: '1px solid #F8FAFC',
                            borderLeft: isBest ? '4px solid var(--school-primary)' : isWorst ? '4px solid #FCA5A5' : '4px solid transparent',
                            background: isBest ? 'rgba(var(--school-primary-rgb),0.03)' : isWorst ? 'rgba(239,68,68,0.02)' : 'transparent',
                          }}
                        >
                          <td style={{ padding: '11px 12px', fontWeight: 600 }}>
                            <Link
                              to="/c/$courseName"
                              params={{ courseName: course.code }}
                              style={{ color: '#0D1B2A', textDecoration: 'none', fontFamily: 'var(--font-family-mono)', fontSize: 12 }}
                              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--school-primary)' }}
                              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0D1B2A' }}
                            >
                              {isBest && <span style={{ fontSize: 11, marginRight: 5, color: 'var(--school-primary)' }}>⭐</span>}
                              {course.code}
                            </Link>
                            {course.description && (
                              <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 400, marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {course.description}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '11px 12px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: 600, color: gpaColor(course.gpa) }}>
                            {course.gpa != null ? course.gpa.toFixed(2) : '—'}
                          </td>
                          <td style={{ padding: '11px 12px', textAlign: 'center', fontFamily: 'var(--font-family-mono)', fontWeight: 700, color: getUDIColor(course.diffScore) }}>
                            {course.diffScore.toFixed(1)}
                          </td>
                          <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{course.passRate}%</td>
                          <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{course.wRate}%</td>
                          <td style={{ padding: '11px 12px', textAlign: 'center', color: '#64748B' }} className="hidden sm:table-cell">{course.sectionCount}</td>
                          <td style={{ padding: '11px 8px', textAlign: 'center' }}>
                            <Link to="/c/$courseName" params={{ courseName: course.code }} style={{ fontSize: 13, color: 'var(--school-primary)', textDecoration: 'none', fontWeight: 700 }}>→</Link>
                          </td>
                        </tr>
                      )
                    })}
                    {courseData.length > TABLE_PREVIEW && !tableExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <button
                            onClick={() => setTableExpanded(true)}
                            style={{ width: '100%', padding: '12px 16px', background: '#F8FAFC', border: 'none', borderTop: '1px dashed #E2E8F0', cursor: 'pointer', fontSize: 13, color: '#94A3B8', fontWeight: 600, textAlign: 'center', fontFamily: 'var(--font-family-sans)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.background = '#F1F5F9' }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.background = '#F8FAFC' }}
                          >
                            Show all {courseData.length} courses ↓
                          </button>
                        </td>
                      </tr>
                    )}
                    {courseData.length > TABLE_PREVIEW && tableExpanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <button
                            onClick={() => setTableExpanded(false)}
                            style={{ width: '100%', padding: '10px 16px', background: '#F8FAFC', border: 'none', borderTop: '1px dashed #E2E8F0', cursor: 'pointer', fontSize: 12, color: '#94A3B8', fontWeight: 600, textAlign: 'center', fontFamily: 'var(--font-family-sans)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = '#64748B' }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8' }}
                          >
                            ↑ Collapse
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ── DEPARTMENTS ── */}
        {DID_LOAD && data.relatedGroups.length > 0 && (
          <div className="mt-8 mb-16">
            <Card>
              <SectionTitle icon="🏛️">Departments</SectionTitle>
              <div className="flex flex-wrap gap-2 mt-4">
                {data.relatedGroups.map(g => (
                  <a
                    key={g.identifier}
                    href={g.href}
                    title={g.description}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '5px 14px',
                      borderRadius: 20,
                      border: '1.5px solid rgba(var(--school-primary-rgb),0.35)',
                      color: 'var(--school-primary)',
                      background: 'rgba(var(--school-primary-rgb),0.06)',
                      textDecoration: 'none',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(var(--school-primary-rgb),0.12)'
                      e.currentTarget.style.borderColor = 'rgba(var(--school-primary-rgb),0.6)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(var(--school-primary-rgb),0.06)'
                      e.currentTarget.style.borderColor = 'rgba(var(--school-primary-rgb),0.35)'
                    }}
                  >
                    {g.shortName ?? g.name}
                  </a>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#94A3B8',
  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center',
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(13,27,42,0.07)', boxShadow: '0 2px 8px rgba(13,27,42,0.05)', padding: 24, ...style }}>
      {children}
    </div>
  )
}

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: string }) {
  return (
    <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0D1B2A', display: 'flex', alignItems: 'center', gap: 8, borderLeft: '3px solid var(--school-primary)', paddingLeft: 12, margin: 0 }}>
      {icon && <span>{icon}</span>}
      {children}
    </h2>
  )
}

function HeroChip({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <span style={{
      fontSize: 12, padding: '4px 11px', borderRadius: 20,
      background: color ? `${color}18` : 'rgba(255,255,255,0.08)',
      border: color ? `1px solid ${color}44` : '1px solid rgba(255,255,255,0.12)',
      color: color ?? 'rgba(255,255,255,0.65)',
      fontWeight: color ? 700 : 500,
      fontFamily: 'var(--font-family-mono)',
    }}>
      {children}
    </span>
  )
}

function QuickStat({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 8px', background: '#F8FAFC', borderRadius: 12 }}>
      <div style={{ fontFamily: 'var(--font-family-mono)', fontSize: 20, fontWeight: 600, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  )
}

const ALERT_STYLES: Record<CourseAlert['severity'], { bg: string; border: string; icon: string; label: string; labelColor: string; textColor: string }> = {
  danger:   { bg: 'rgba(220,38,38,0.06)',  border: 'rgba(220,38,38,0.25)',  icon: '⚠️', label: 'HEADS UP',      labelColor: '#DC2626', textColor: '#7F1D1D' },
  warning:  { bg: 'rgba(217,119,6,0.06)',  border: 'rgba(217,119,6,0.25)',  icon: '📢', label: 'WORTH KNOWING', labelColor: '#D97706', textColor: '#78350F' },
  positive: { bg: 'rgba(5,150,105,0.06)',  border: 'rgba(5,150,105,0.25)',  icon: '✅', label: 'GOOD NEWS',     labelColor: '#059669', textColor: '#064E3B' },
  info:     { bg: 'rgba(var(--school-primary-rgb),0.06)',  border: 'rgba(var(--school-primary-rgb),0.25)',  icon: 'ℹ️', label: 'FYI',           labelColor: '#0891B2', textColor: '#164E63' },
}

function ProfAlertBanner({ alert }: { alert: CourseAlert }) {
  const s = ALERT_STYLES[alert.severity]
  return (
    <div style={{ marginTop: 24, borderLeft: `4px solid ${s.border}`, background: s.bg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderRadius: '0 10px 10px 0' }}>
      <span style={{ fontSize: 20, flexShrink: 0, lineHeight: 1 }}>{s.icon}</span>
      <p style={{ fontSize: 14, fontWeight: 600, color: s.textColor, margin: 0, lineHeight: 1.5 }}>
        {alert.message}
      </p>
    </div>
  )
}

type CourseRowData = {
  code: string; description: string; grades: GradeCounts
  gpa: number | null; diffScore: number; sectionCount: number; total: number
}

function CourseRow({ course }: { course: CourseRowData }) {
  const gColor = course.gpa != null ? (
    course.gpa >= 3.5 ? '#059669' : course.gpa >= 2.5 ? '#10B981' : course.gpa >= 1.5 ? '#D97706' : '#DC2626'
  ) : '#94A3B8'
  const dColor = getUDIColor(course.diffScore)
  return (
    <div style={{ border: '1px solid #F1F5F9', borderRadius: 10, padding: '10px 12px', background: '#FAFAFA', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link
            to="/c/$courseName"
            params={{ courseName: course.code }}
            style={{ fontFamily: 'var(--font-family-mono)', fontSize: 13, fontWeight: 700, color: 'var(--school-primary)', textDecoration: 'none' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0D1B2A' }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--school-primary)' }}
          >
            {course.code}
          </Link>
          {course.description && (
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 500, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {course.description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
          <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 13, fontWeight: 700, color: gColor }}>
            {course.gpa != null ? course.gpa.toFixed(2) : '—'}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: dColor, background: `${dColor}15`, padding: '2px 6px', borderRadius: 5 }}>
            {course.diffScore.toFixed(1)} {getUDILabel(course.diffScore)}
          </span>
          <span style={{ fontSize: 11, color: '#94A3B8' }}>{course.sectionCount}×</span>
        </div>
      </div>
      {course.total > 0 && (
        <div style={{ display: 'flex', width: '100%', gap: 1, height: 4, borderRadius: 3, overflow: 'hidden' }}>
          {(Object.keys(GRADE_COLORS) as (keyof typeof GRADE_COLORS)[])
            .map(g => ({ g, pct: (course.grades[g as keyof GradeCounts] ?? 0) / course.total * 100 }))
            .filter(s => s.pct >= 0.5)
            .map(({ g, pct }) => (
              <div key={g} style={{ width: `${pct}%`, backgroundColor: GRADE_COLORS[g], minWidth: 2 }} />
            ))}
        </div>
      )}
    </div>
  )
}

function CourseRowSkeleton() {
  return (
    <div style={{ border: '1px solid #F1F5F9', borderRadius: 10, padding: '10px 12px', background: '#FAFAFA' }}>
      <div className="animate-pulse">
        <div style={{ height: 13, background: '#E2E8F0', borderRadius: 4, width: '55%', marginBottom: 6 }} />
        <div style={{ height: 4, background: '#E2E8F0', borderRadius: 3 }} />
      </div>
    </div>
  )
}

function CourseSortTh({ label, sortKey, current, dir, onSort, align, className }: {
  label: string; sortKey: CourseSortKey; current: CourseSortKey; dir: SortDir
  onSort: (k: CourseSortKey) => void; align?: 'left' | 'center'; className?: string
}) {
  const active = current === sortKey
  return (
    <th
      className={className}
      onClick={() => onSort(sortKey)}
      style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700, color: active ? '#0D1B2A' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: align ?? 'center', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}{active ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  )
}

function NotFoundComponent() {
  const { instructorName } = Route.useParams()
  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>

      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <div style={{ fontSize: 48, marginBottom: 16 }}>👤</div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>Professor not found</h1>
        <p style={{ fontSize: 15, color: '#64748B', marginBottom: 24 }}>
          No record for <code style={{ background: '#F1F5F9', padding: '2px 8px', borderRadius: 6, fontSize: 13 }}>{instructorName}</code>
        </p>
        <Link to="/" style={{ display: 'inline-block', padding: '10px 20px', background: 'var(--school-primary)', color: '#0D1B2A', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>
          Back to home
        </Link>
      </div>
    </div>
  )
}
